import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  useMeQuery,
  useLogoutMutation,
  useListPtoProjectsQuery,
  useListProgressObjectsQuery,
  useGetDiskInfoQuery,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import { useMediaQuery } from '@shared/lib/useMediaQuery';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import { clearTokens } from '@features/auth/model/slice';
import { ThemeToggle } from './ThemeToggle';
import { RealtimeBadge } from './RealtimeBadge';
import { Button } from '@shared/ui/Button';
import { Brand } from '@shared/ui/Brand';
import { Skeleton } from '@shared/ui/Skeleton';
import { IconChat, IconHardDrive, IconPanelLeft } from '@shared/ui/icons';
import { agentHref, shortAgentLabel } from '@shared/agents/labels';
import { NotificationsBell } from '@widgets/Notifications/NotificationsBell';
import { UpdatesBell } from '@widgets/Updates/UpdatesBell';
import { UpdatesModal } from '@widgets/Updates/UpdatesModal';
import { UpdatesPopup } from '@widgets/Updates/UpdatesPopup';
import { RiskDispatchPopup } from '@widgets/RiskDispatch/RiskDispatchPopup';
import { MaintenanceBanner } from '@widgets/Maintenance/MaintenanceBanner';
import { AssistantDrawer } from '@widgets/Assistant/AssistantDrawer';

/** Ширина развёрнутого меню. Вынесена в константу: её знают и сетка оболочки,
 *  и внутренняя обёртка меню, которая при сворачивании не должна сжиматься. */
const NAV_WIDTH = '248px';

/** Ключ в localStorage: меню свернул ('1') или развернул ('0') сам человек. Пишется только
 *  по его нажатию: без выбора меню следует ширине окна. */
const NAV_CHOICE_KEY = 'osnova.nav.choice';
/** Прежний ключ: в него «0» записывался при каждом входе, выбором человека была только «1». */
const LEGACY_NAV_COLLAPSED_KEY = 'osnova.nav.collapsed';
/** Окно не шире этого — меню без выбора человека свёрнуто: раскрытое съело бы ширину страницы. */
const NARROW_SCREEN_QUERY = '(max-width: 1024px)';
/** Ключ в localStorage: пользовательский порядок агентов в боковой панели. */
const AGENT_ORDER_KEY = 'osnova.nav.agentOrder';

/** Свернул или развернул ли меню сам человек; `null` — не выбирал. localStorage может быть
 *  недоступен (приватное окно, запрет на данные сайта) — тогда выбора нет. */
function readNavChoice(): boolean | null {
  try {
    const choice = localStorage.getItem(NAV_CHOICE_KEY);
    if (choice === '1' || choice === '0') return choice === '1';
    return localStorage.getItem(LEGACY_NAV_COLLAPSED_KEY) === '1' ? true : null;
  } catch {
    return null;
  }
}

const Shell = styled.div<{ $navCollapsed: boolean }>`
  display: grid;
  grid-template-columns: ${({ $navCollapsed }) => ($navCollapsed ? '0px' : NAV_WIDTH)} 1fr;
  /* Первый ряд — плашка об обновлении (MaintenanceBanner). Когда работ нет,
     компонент ничего не рендерит и ряд auto схлопывается в ноль. */
  grid-template-rows: auto 64px 1fr;
  grid-template-areas:
    'banner banner'
    'header header'
    'sidebar content';
  height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  transition: grid-template-columns ${({ theme }) => theme.motion.base}
    ${({ theme }) => theme.motion.easeOut};

  /* Пользователь просил систему не анимировать — уважаем. */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Header = styled.header`
  grid-area: header;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${({ theme }) => theme.spacing(6)};
  background: ${({ theme }) => theme.colors.backgroundElevated}cc;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  position: sticky;
  top: 0;
  z-index: ${({ theme }) => theme.z.sticky};
`;

const Sidebar = styled.nav<{ $collapsed: boolean }>`
  grid-area: sidebar;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  overflow-y: auto;
  /* Колонка сетки схлопывается до нуля — содержимое не должно ни сжиматься,
     ни выпирать: прячем то, что вышло за границу. */
  overflow-x: hidden;
  /* Свёрнутое меню убираем из фокуса и из чтения скринридером: иначе Tab
     уводит каретку в невидимый список из двадцати ссылок. Скрываем после
     анимации, разворачиваем сразу — иначе видно пустую панель. */
  visibility: ${({ $collapsed }) => ($collapsed ? 'hidden' : 'visible')};
  transition: visibility 0s linear
    ${({ $collapsed, theme }) => ($collapsed ? theme.motion.base : '0s')};
`;

/** Содержимое меню фиксированной ширины: при сворачивании колонка сетки едет
 *  к нулю, а пункты остаются прежними и просто уезжают за край. Без этого
 *  текст пунктов на глазах переносился бы по словам. */
const SidebarInner = styled.div`
  width: ${NAV_WIDTH};
  padding: ${({ theme }) => theme.spacing(5)} 0;
`;

/** Кнопка сворачивания меню — слева от логотипа, как в почтовых клиентах. */
const NavToggle = styled.button<{ $collapsed: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  background: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition:
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.surface};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  /* Свёрнутое состояние подсвечиваем: иначе непонятно, меню спрятано или
     его просто нет. */
  ${({ $collapsed, theme }) =>
    $collapsed &&
    `
    color: ${theme.colors.primary};
  `}
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const ProjectSwitcherWrap = styled.div<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : theme.colors.surface};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-left: ${({ theme }) => theme.spacing(2)};

  @media (max-width: 760px) {
    display: none;
  }
`;

const ProjectSwitcherLabel = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
  white-space: nowrap;
`;

const ProjectSwitcherSelect = styled.select`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  max-width: 240px;
  cursor: pointer;
  outline: none;
`;

const NavItem = styled(NavLink)<{ $dragging?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2.5)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(6)};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  user-select: none;
  opacity: ${({ $dragging }) => ($dragging ? 0.55 : 1)};
  transition:
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    opacity ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 12%;
    bottom: 12%;
    width: 3px;
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 0 ${({ theme }) => theme.radius.pill} ${({ theme }) => theme.radius.pill} 0;
    transform: scaleY(0);
    transform-origin: center;
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.spring};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.surface};
  }

  &.active {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primarySoft};
    font-weight: 600;
  }

  &.active::before {
    transform: scaleY(1);
  }
`;

// «Диск» — отдельное окно-хранилище, а не раздел: значок накопителя фирменным
// цветом и чуть плотнее подпись. Фон не трогаем — им выделен открытый раздел.
const DiskNavItem = styled(NavItem)`
  justify-content: flex-start;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const DragGrip = styled.span`
  font-size: 12px;
  letter-spacing: -1px;
  color: ${({ theme }) => theme.colors.text.muted};
  opacity: 0;
  cursor: grab;
  transition: opacity ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  ${NavItem}:hover & {
    opacity: 0.65;
  }

  &:active {
    cursor: grabbing;
  }
`;

const SidebarHeading = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
  padding: 0 ${({ theme }) => theme.spacing(6)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Content = styled.main`
  grid-area: content;
  padding: ${({ theme }) => theme.spacing(8)};
  overflow: auto;

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing(4)};
  }
`;

const UserBlock = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.3;
`;

const UserName = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradient.brand};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  box-shadow: ${({ theme }) => theme.shadow.sm};
`;

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

// Скелетоны-плейсхолдеры для группы «Агенты» пока /auth/me грузится.
// Прячут «дырку» в сайдбаре после первого логина / cold-start backend.
const NavSkeleton = styled(Skeleton)`
  margin: 8px ${({ theme }) => theme.spacing(6)};
  height: 18px;
`;

/**
 * Fallback-кик обязательной 2FA: если сервер требует 2FA (mfa_enforce), а у
 * юзера её нет — выкидываем на /login, где сработает enforcement-настройка.
 * Страховка к token_version-mass-kick для тех, кто вошёл ДО включения
 * enforcement (их access ещё валиден по tv, но 2FA отсутствует). Нужна каждой
 * защищённой оболочке — и AppShell, и страницам без неё (ОСНОВА.Эксперт).
 */
export function useMfaEnforcement() {
  const { data: ctx } = useMeQuery();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  useEffect(() => {
    if (ctx?.mfa_enforce && !ctx.totp_enabled) {
      dispatch(clearTokens());
      navigate('/login', { replace: true });
    }
  }, [ctx?.mfa_enforce, ctx?.totp_enabled, dispatch, navigate]);
}

export function AppShell() {
  const { data: ctx, isLoading: isMeLoading } = useMeQuery();
  const [logout] = useLogoutMutation();
  // ОСНОВА.Диск: пункт меню есть, только если Диск доступен человеку. Нет ручки
  // (404) или ошибка — пункта нет.
  const { data: diskInfo } = useGetDiskInfoQuery();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // refresh нужен только чтобы отправить его в /auth/logout — бэкенд отзовёт
  // оба токена, иначе украденный refresh пережил бы logout (аудит 20.07 §10).
  const refreshToken = useAppSelector((s) => s.auth.refreshToken);
  // Состояние модалки «Что нового» живёт здесь: его открывают и колокольчик,
  // и живая плашка (UpdatesBanner).
  const [updatesOpen, setUpdatesOpen] = useState(false);
  // Помощник открывается панелью поверх текущей страницы — так он знает раздел.
  const [assistantOpen, setAssistantOpen] = useState(false);
  // Свёрнутое меню запоминаем между визитами: кто работает в одном разделе
  // (диспетчер, кадровик), сворачивает его один раз и больше не возвращается
  // к этому. Пока человек сам не выбирал, на узком окне меню свёрнуто, на
  // широком — раскрыто.
  const narrowScreen = useMediaQuery(NARROW_SCREEN_QUERY);
  const [navChoice, setNavChoice] = useState<boolean | null>(readNavChoice);
  const navCollapsed = navChoice ?? narrowScreen;

  function toggleNav() {
    const next = !navCollapsed;
    setNavChoice(next);
    try {
      localStorage.setItem(NAV_CHOICE_KEY, next ? '1' : '0');
    } catch {
      // Запись недоступна — выбор просто не переживёт перезагрузку.
    }
  }

  useMfaEnforcement();

  async function handleLogout() {
    try {
      await logout({ refreshToken }).unwrap();
    } catch {
      // ignore
    }
    dispatch(clearTokens());
    navigate('/login', { replace: true });
  }

  const accessibleAgents = (ctx?.agents ?? []).filter((a) => a.can_read);
  // Доступ к UI диспетчера получают:
  //   * lawyer/admin — через statut (полные права на юр.модуль);
  //   * dispatcher_operator — точечно через agent_code='dispatcher'.
  const hasDispatcherAccess = accessibleAgents.some(
    (a) => a.agent_code === 'dispatcher' || a.agent_code === 'statut',
  );
  // ОСНОВА.Эксперт — функция Руководителя / Гендиректора, доступ тот же. Открывается отдельной
  // страницей без меню; откуда пришли — туда и вернёт её кнопка «В ОСНОВУ».
  const hasExpertAccess = accessibleAgents.some(
    (a) => a.agent_code === 'progress' || a.agent_code === 'ceo',
  );
  // Бэкенд check_ceo_access пускает в /agents/ceo при наличии 'ceo' ИЛИ 'progress'.
  // Если в agents[] пришёл 'progress', но не 'ceo', дорендериваем пункт без дубля.
  const hasCeoFallbackAccess =
    !accessibleAgents.some((a) => a.agent_code === 'ceo') &&
    accessibleAgents.some((a) => a.agent_code === 'progress');
  const hasFinanceFallbackAccess =
    !accessibleAgents.some((a) => a.agent_code === 'finance') &&
    accessibleAgents.some((a) => a.agent_code === 'ceo' || a.agent_code === 'econ');
  const hasPtoAccess = accessibleAgents.some((a) => a.agent_code === 'pto');

  // Пользовательский порядок агентов в боковой панели (сохраняется в localStorage).
  const [agentOrder, setAgentOrder] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(AGENT_ORDER_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
    } catch {
      return [];
    }
  });
  const [draggingCode, setDraggingCode] = useState<string | null>(null);
  const didDragRef = useRef(false);

  const availableAgentItems = useMemo(() => {
    const list: Array<{ code: string; label: string; to: string }> = [];
    const seen = new Set<string>();
    const push = (code: string) => {
      if (seen.has(code)) return;
      seen.add(code);
      list.push({
        code,
        label: shortAgentLabel(code),
        to: agentHref(code),
      });
    };
    for (const a of accessibleAgents) {
      // Архивариус живёт в окне Диска: без Диска пункта нет.
      if (a.agent_code === 'archivist' && !diskInfo?.available) continue;
      if (a.agent_code !== 'dispatcher') {
        push(a.agent_code);
      }
    }
    if (hasCeoFallbackAccess) push('ceo');
    if (hasFinanceFallbackAccess) push('finance');
    if (hasDispatcherAccess) push('dispatcher');
    return list;
  }, [accessibleAgents, hasCeoFallbackAccess, hasFinanceFallbackAccess, hasDispatcherAccess, diskInfo?.available]);

  const orderedAgentItems = useMemo(() => {
    if (agentOrder.length === 0) return availableAgentItems;
    const indexMap = new Map(agentOrder.map((code, idx) => [code, idx]));
    return [...availableAgentItems].sort((a, b) => {
      const ia = indexMap.get(a.code);
      const ib = indexMap.get(b.code);
      if (ia !== undefined && ib !== undefined) return ia - ib;
      if (ia !== undefined) return -1;
      if (ib !== undefined) return 1;
      return 0;
    });
  }, [availableAgentItems, agentOrder]);

  function saveAgentOrder(nextOrder: string[]) {
    setAgentOrder(nextOrder);
    try {
      localStorage.setItem(AGENT_ORDER_KEY, JSON.stringify(nextOrder));
    } catch {
      // ignore storage errors
    }
  }

  function moveAgent(fromCode: string, toCode: string) {
    if (fromCode === toCode) return;
    const currentCodes = orderedAgentItems.map((item) => item.code);
    const fromIdx = currentCodes.indexOf(fromCode);
    const toIdx = currentCodes.indexOf(toCode);
    if (fromIdx === -1 || toIdx === -1) return;
    const next = [...currentCodes];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    saveAgentOrder(next);
  }
  const { activeProjectId, activeProjectName, setActiveProjectId } = useActiveProjectContext();
  const ptoProjectsQ = useListPtoProjectsQuery(undefined, { skip: !hasPtoAccess });
  const progressObjectsQ = useListProgressObjectsQuery(undefined, {
    skip: hasPtoAccess || !hasExpertAccess,
  });
  const headerProjects = useMemo(() => {
    if (ptoProjectsQ.data?.items && ptoProjectsQ.data.items.length > 0) {
      return ptoProjectsQ.data.items.map((p) => ({ id: p.id, name: p.name }));
    }
    if (progressObjectsQ.data && progressObjectsQ.data.length > 0) {
      return progressObjectsQ.data.map((o) => ({ id: o.id, name: o.name }));
    }
    return [];
  }, [ptoProjectsQ.data, progressObjectsQ.data]);

  useEffect(() => {
    if (!activeProjectId || headerProjects.length === 0) return;
    const matched = headerProjects.find((p) => p.id === activeProjectId);
    if (matched && matched.name && matched.name !== activeProjectName) {
      setActiveProjectId(activeProjectId, { name: matched.name });
    }
  }, [activeProjectId, activeProjectName, headerProjects, setActiveProjectId]);

  return (
    <Shell $navCollapsed={navCollapsed}>
      <MaintenanceBanner />
      <Header>
        <HeaderLeft>
          <NavToggle
            type="button"
            $collapsed={navCollapsed}
            onClick={toggleNav}
            aria-expanded={!navCollapsed}
            aria-controls="main-nav"
            aria-label={navCollapsed ? 'Показать меню' : 'Свернуть меню'}
            title={navCollapsed ? 'Показать меню' : 'Свернуть меню'}
          >
            <IconPanelLeft size={18} />
          </NavToggle>
          <Brand $size="md" $animate />
          {headerProjects.length > 0 && (
            <ProjectSwitcherWrap
              $active={Boolean(activeProjectId)}
              title="Активный строительный объект (сквозной контекст разделов ОСНОВЫ)"
            >
              <ProjectSwitcherLabel>Объект:</ProjectSwitcherLabel>
              <ProjectSwitcherSelect
                value={activeProjectId ?? ''}
                aria-label="Активный строительный объект"
                onChange={(e) => {
                  const val = e.target.value;
                  const proj = headerProjects.find((p) => p.id === val);
                  setActiveProjectId(val || null, { name: proj?.name });
                }}
              >
                <option value="">— все объекты —</option>
                {headerProjects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </ProjectSwitcherSelect>
            </ProjectSwitcherWrap>
          )}
        </HeaderLeft>
        <UserBlock>
          <Button
            $variant="ghost"
            $size="sm"
            onClick={() => setAssistantOpen((v) => !v)}
            aria-expanded={assistantOpen}
            title="Спросить Помощника: как работать в ОСНОВЕ и рабочие вопросы"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <IconChat size={16} />
            <span>Помощник</span>
          </Button>
          <RealtimeBadge />
          <NotificationsBell />
          <UpdatesBell onOpen={() => setUpdatesOpen(true)} />
          <ThemeToggle />
          {ctx?.user && (
            <>
              <UserInfo>
                <UserName>{ctx.user.full_name}</UserName>
                <span>{ctx.user.department ?? 'без отдела'}</span>
              </UserInfo>
              <Avatar>{initials(ctx.user.full_name)}</Avatar>
            </>
          )}
          <Button $variant="ghost" $size="sm" onClick={handleLogout}>
            Выйти
          </Button>
        </UserBlock>
      </Header>
      <Sidebar id="main-nav" $collapsed={navCollapsed}>
        <SidebarInner>
          <SidebarHeading>Главное</SidebarHeading>
          <NavItem to="/" end>
            Дашборд
          </NavItem>
          <NavItem to="/security">Безопасность</NavItem>
          <NavItem to="/assistant">Помощник</NavItem>
          {hasExpertAccess && (
            <NavItem to="/expert" state={{ from: location.pathname + location.search }}>
              Эксперт
            </NavItem>
          )}
          <NavItem to="/instructions">Мои поручения</NavItem>
          <NavItem to="/pay-requests">Заявки на оплату</NavItem>
          {diskInfo?.available && (
            // Обычный щелчок — Диск в этой же вкладке, как Эксперт; Ctrl и
            // средняя кнопка — в новой (это обычная ссылка).
            <DiskNavItem to="/disk" state={{ from: location.pathname + location.search }}>
              <IconHardDrive size={18} />
              <span>Диск</span>
            </DiskNavItem>
          )}
          <div style={{ height: 20 }} />
          <SidebarHeading>Агенты</SidebarHeading>
          {isMeLoading ? (
            <>
              <NavSkeleton $width="60%" />
              <NavSkeleton $width="70%" />
              <NavSkeleton $width="55%" />
            </>
          ) : orderedAgentItems.length === 0 ? (
            <div
              style={{
                padding: '8px 24px',
                fontSize: 12,
                color: 'var(--text-muted, #888)',
                fontStyle: 'italic',
              }}
            >
              Нет доступных агентов
            </div>
          ) : (
            orderedAgentItems.map((item) => (
              <NavItem
                key={item.code}
                to={item.to}
                // Окно Диска (Архивариус) вернёт «Вернуться в ОСНОВУ» сюда.
                state={item.code === 'archivist' ? { from: location.pathname + location.search } : undefined}
                draggable
                $dragging={draggingCode === item.code}
                title="Зажмите и перетащите, чтобы изменить порядок"
                onClick={(e) => {
                  if (didDragRef.current) {
                    e.preventDefault();
                  }
                }}
                onDragStart={(e) => {
                  didDragRef.current = false;
                  setDraggingCode(item.code);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', item.code);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (draggingCode && draggingCode !== item.code) {
                    didDragRef.current = true;
                    moveAgent(draggingCode, item.code);
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const fromCode = draggingCode || e.dataTransfer.getData('text/plain');
                  if (fromCode && fromCode !== item.code) {
                    didDragRef.current = true;
                    moveAgent(fromCode, item.code);
                  }
                  setDraggingCode(null);
                }}
                onDragEnd={() => {
                  setDraggingCode(null);
                  setTimeout(() => {
                    didDragRef.current = false;
                  }, 50);
                }}
              >
                <span>{item.label}</span>
                <DragGrip aria-hidden="true">⋮⋮</DragGrip>
              </NavItem>
            ))
          )}
          {/* admin-секция: видна только пользователям с ролью `admin`. */}
          {ctx?.roles?.includes('admin') && (
            <>
              <div style={{ height: 20 }} />
              <SidebarHeading>Администрация</SidebarHeading>
              <NavItem to="/admin" end>
                Обзор
              </NavItem>
              <NavItem to="/admin/users">Пользователи</NavItem>
            <NavItem to="/admin/services">Службы</NavItem>
              <NavItem to="/admin/system">Сервер</NavItem>
              <NavItem to="/admin/announcements">Обновления</NavItem>
              <NavItem to="/admin/audit">Журнал</NavItem>
            </>
          )}
        </SidebarInner>
      </Sidebar>
      <Content>
        <Outlet />
      </Content>
      <UpdatesModal open={updatesOpen} onClose={() => setUpdatesOpen(false)} />
      <UpdatesPopup onOpen={() => setUpdatesOpen(true)} suppressed={updatesOpen} />
      <RiskDispatchPopup />
      {assistantOpen && <AssistantDrawer onClose={() => setAssistantOpen(false)} />}
    </Shell>
  );
}
